import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CAccordion, CAccordionBody, CAccordionHeader, CAccordionItem } from '@coreui/react-pro'
import FEATURES from '../../../../../utils/featuresConstants'
import { updateMDM } from '../../../../../redux/sidePanel/sidePanelThunk'
import { getIsAi } from '../../../../../utils/sharedFeaturesFunctions'
import { debounce } from 'lodash'

const mdmDefaults = {
  'Number and Complexity of Problems': {
    Minimal:
      'Self-limited or minor problems that don’t require significant intervention or management.',
    Low: 'Chronic conditions that are stable, or acute conditions that require minimal intervention.',
    Moderate:
      'New or worsening conditions that require more extensive workup, testing, or management.',
    High: 'Acute, serious conditions, or conditions that require immediate, extensive intervention or coordination.',
  },
  Data: {
    Minimal: 'No data review or minimal review required.',
    Low: 'Review of a few documents or data points (e.g., lab results, X-rays, etc.).',
    Moderate:
      'Review of multiple pieces of data, including lab results, X-rays, or complex medical records.',
    High: 'Review of a substantial amount of data, such as medical records, multiple diagnostic tests, or consultations with specialists.',
  },
  Risk: {
    Minimal: 'No significant risks or very low risk (e.g., routine exams, minor lab tests).',
    Low: 'Low risk of complications or adverse outcomes (e.g., simple tests, non-invasive procedures).',
    Moderate:
      'Conditions with a moderate risk of complications or morbidity, or decisions that involve a moderate level of risk (e.g., prescribing medications that have side effects).',
    High: 'High risk of morbidity or mortality due to the patient’s condition, treatments, or the need for urgent intervention (e.g., major surgery, acute or life-threatening conditions).',
  },
}

const mdmDebouncer = debounce((funcToDebounce) => {
  funcToDebounce()
}, 2500)

function mdmConversion(newVal) {
  if (typeof newVal === 'string') {
    switch (newVal) {
      case 'High':
        return 3
      case 'Moderate':
        return 2
      case 'Low':
        return 1
      default:
        return 0
    }
  } else {
    // is number
    switch (newVal) {
      case 3:
        return 'High'
      case 2:
        return 'Moderate'
      case 1:
        return 'Low'
      default:
        return 'Minimal'
    }
  }
}

function calculateUserMDM(mdm) {
  if (!mdm) {
    return null
  }
  const complex = mdm?.complexity?.user_level ? mdmConversion(mdm?.complexity?.user_level) : -1
  const data = mdm?.data?.user_level ? mdmConversion(mdm?.data?.user_level) : -1
  const risk = mdm?.risk?.user_level ? mdmConversion(mdm?.risk?.user_level) : -1

  if (complex > -1 && data > -1 && risk > -1) {
    // this copies fusions logic on how we determine MDM
    // so we can just save locally white the fusion-save call goes out
    const sorted = [complex, data, risk].sort()
    return mdmConversion(sorted[1])
  }

  return null
}

const HIDE_MDM_CPTS = ['99291', '99292', '99238', '99239', '99315', '99316']

export default function PanelMDMCard() {
  const dispatch = useDispatch()
  const features = useSelector((state) => state.user?.features) || []

  const isReadOnly = useSelector((state) => state.sidePanel.isReadOnly)
  const panelMdm = useSelector(
    (state) => state.sidePanel.panelEncounter?.coding_ai?.anthropic?.mdmelements,
  )
  const anthropicEMCode = useSelector(
    (state) => state.sidePanel.panelEncounter?.coding_ai?.anthropic?.emcode,
  )
  const coding_ai = useSelector((state) => state.sidePanel.panelEncounter?.coding_ai)
  const cpts = useSelector((state) => state.sidePanel.panelEncounter?.procedureCodes)
  const insufficientInfo = '1' === '0' && coding_ai?.status === 'failed' // ALWAYS HIDE FOR NOW

  const emCode = cpts?.[0]?.code
  const isAI = Boolean(coding_ai)

  const [isOpen, setIsOpen] = useState(false)
  const [mdmValues, setMdmValues] = useState(panelMdm || {})

  useEffect(() => {
    async function setMDM() {
      const mdmAction = await dispatch(updateMDM({}))
      if (!mdmAction?.error) {
        setMdmValues(mdmAction.payload)
      } else {
        setMdmValues((prev) => ({ ...prev, has_mdm: false }))
      }
    }

    if (isAI) {
      // wait for save visit call to end
      setTimeout(setMDM, 1000)
    }
    // if emCode changes, reevaluate if MDM is valid
  }, [dispatch, isAI, emCode])

  const onMDMSlide = (mdmType, newMdmVal) => {
    setMdmValues((prev) => {
      const newLevel = mdmConversion(newMdmVal)
      const newObj = {
        ...prev,
        [mdmType]: {
          ...(prev?.[mdmType] || {}),
          user_level: newLevel,
        },
      }

      mdmDebouncer(() => dispatch(updateMDM(newObj)))

      return newObj
    })
  }

  const shouldShowMDM = !HIDE_MDM_CPTS?.includes(String(anthropicEMCode))
  const userMDM = calculateUserMDM(mdmValues)

  const canShowMDM = mdmValues?.has_mdm || panelMdm?.overall?.level

  return (
    getIsAi() &&
    features?.includes(FEATURES.DesktopAI) &&
    ((canShowMDM && shouldShowMDM) || insufficientInfo) &&
    Boolean(cpts.find((cpt) => cpt.em_code)) && (
      <CAccordion id={'PanelMDMCard'}>
        <CAccordionItem>
          <CAccordionHeader
            className={insufficientInfo ? 'hideBtn' : ''}
            onClick={() => {
              setIsOpen((prev) => !prev)
            }}
          >
            <div className={'d-flex flex-column'}>
              <span>
                Overall MDM:{' '}
                {insufficientInfo &&
                  'MDM level cannot be determined. Additional documentation required.'}
                <span
                  className={
                    !panelMdm?.overall?.user_level ||
                    panelMdm?.overall?.user_level === panelMdm?.overall?.level
                      ? 'ai'
                      : ''
                  }
                >
                  {userMDM || panelMdm?.overall?.user_level || panelMdm?.overall?.level}
                </span>
              </span>
              {isOpen && <span className={'mdm-rationale'}>{panelMdm?.overall?.rationale}</span>}
            </div>
            {isOpen ? (
              <i className="fa-solid fa-chevron-up"></i>
            ) : (
              <i className="fa-solid fa-chevron-down"></i>
            )}
          </CAccordionHeader>
          <CAccordionBody id={'accordion-body'}>
            <div className={'display-box d-flex flex-column gap-2'}>
              <MDMSection
                mdm={mdmValues?.complexity}
                title={'Number and Complexity of Problems'}
                index={1}
                onChange={(newNum) => onMDMSlide('complexity', newNum)}
                isReadOnly={isReadOnly}
              />
              <MDMSection
                mdm={mdmValues?.data}
                title={'Data'}
                index={2}
                onChange={(newNum) => onMDMSlide('data', newNum)}
                isReadOnly={isReadOnly}
              />
              <MDMSection
                mdm={mdmValues?.risk}
                title={'Risk'}
                index={3}
                onChange={(newNum) => onMDMSlide('risk', newNum)}
                isReadOnly={isReadOnly}
              />
            </div>
          </CAccordionBody>
        </CAccordionItem>
      </CAccordion>
    )
  )
}

function MDMSection({ mdm = {}, title, index, onChange, isReadOnly }) {
  const level = mdm?.level || 'Minimal'
  const user_level = mdm?.user_level || mdm?.level || 'Minimal'

  return (
    <div className={'MDMSection'}>
      <div className={'d-flex flex-row gap-2'}>
        <div className="number">{index}</div>
        {title}
      </div>

      <input
        disabled={isReadOnly}
        type="range"
        id="volume"
        name="volume"
        min="0"
        max="3"
        value={mdmConversion(user_level)}
        onChange={(e) => {
          onChange(Number(e.target.value))
        }}
      />
      <div className="range-titles">
        <div className={level === 'Minimal' ? 'ai' : ''}>Min</div>
        <div className={level === 'Low' ? 'ai' : ''}>Low</div>
        <div className={level === 'Moderate' ? 'ai' : ''}>Mod</div>
        <div className={level === 'High' ? 'ai' : ''}>High</div>
      </div>
      <div className={'mdm-range-description'}>
        {level === user_level ? mdm?.rationale : mdmDefaults?.[title]?.[user_level]}
      </div>
    </div>
  )
}
